/**
 * Welche Organisation zeigt der Verbrauchsbalken? (KOL-94)
 *
 * Die Seitenleiste ist global, ein Nutzer kann aber in mehreren Organisationen
 * sein. Summieren wäre falsch — es sind getrennte Verträge mit getrennten
 * Kontingenten. Deshalb: der geöffnete Kanal bestimmt die Organisation, und
 * ohne Kanalbezug nur dann etwas anzeigen, wenn es nichts zu verwechseln gibt.
 */
export function resolveUsageOrgId(params: {
  activeChannelOrgId: string | null;
  userOrgIds: string[];
}): string | null {
  if (params.activeChannelOrgId) return params.activeChannelOrgId;
  return params.userOrgIds.length === 1 ? params.userOrgIds[0]! : null;
}

/**
 * Ab dieser Auslastung wird der Verbrauch überhaupt angezeigt (KOL-132).
 *
 * Darunter ist die Zahl für niemanden eine Entscheidungsgrundlage — sie steht
 * nur im Weg und lädt zum Falschlesen ein. Erst wenn das Kontingent knapp
 * wird, ist der Hinweis etwas wert.
 */
export const ANZEIGE_SCHWELLE_PROZENT = 90;

/**
 * Auslastung in ganzen Prozent, oder null ohne Kontingent.
 *
 * Ohne Kontingent gibt es keinen Anteil — ein Verbrauch ohne Bezugsgröße wäre
 * eine Zahl ohne Aussage. Bewusst gerundet: Schwelle und Anzeige müssen
 * denselben Wert benutzen, sonst stünde irgendwann „90 %" bei ausgeblendeter
 * Anzeige oder umgekehrt.
 */
export function usagePercent(used: number, quota: number | null): number | null {
  if (!quota || quota <= 0) return null;
  return Math.round((used / quota) * 100);
}

/** Ist die Anzeigeschwelle erreicht? (KOL-132) */
export function zeigeVerbrauch(used: number, quota: number | null): boolean {
  const prozent = usagePercent(used, quota);
  return prozent !== null && prozent >= ANZEIGE_SCHWELLE_PROZENT;
}

export type UsageState = "normal" | "warn" | "over";

/**
 * Ab 90 % Hinweis, ab 100 % überschritten. Ohne Kontingent gibt es nichts zu
 * warnen.
 *
 * „normal" bleibt erhalten, obwohl die Anzeige unterhalb der Schwelle gar
 * nicht mehr erscheint: Die Farbe ist eine Frage der Auslastung, die
 * Sichtbarkeit eine getrennte. Wer die Schwelle einmal senkt, soll nicht
 * plötzlich ungefärbte Balken bekommen.
 */
export function usageState(used: number, quota: number | null): UsageState {
  const prozent = usagePercent(used, quota);
  if (prozent === null) return "normal";
  if (prozent >= 100) return "over";
  if (prozent >= ANZEIGE_SCHWELLE_PROZENT) return "warn";
  return "normal";
}
